import React, { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BsFilterLeft, BsX } from "react-icons/bs";
import { HiOutlineArrowRight } from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";
import { getProducts } from "../features/products/productSlice";
import Product from "../components/Product";
import ProductSkeleton from "../components/ProductSkeleton";
import { motion, AnimatePresence } from "framer-motion";
import AnimatedBackground from "../components/AnimatedBackground";

function Catalog() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const initialCategory = searchParams.get("category") || "All";

  const [sortBy, setSortBy] = React.useState("title-ascending");
  const [activeCategory, setActiveCategory] = React.useState(initialCategory);
  const [visibleProducts, setVisibleProducts] = React.useState(6);
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);

  const { products, isError, message, isLoading } = useSelector(
    (state) => state.products
  );

  const { cart } = useSelector((state) => {
    return state.cart;
  });
  // Calculate items per row based on screen size
  const getItemsPerRow = () => {
    if (typeof window !== "undefined") {
      if (window.innerWidth >= 1024) return 3; // lg and up
      if (window.innerWidth >= 640) return 2; // sm and up
      return 2; // mobile
    }
    return 3;
  };

  // Calculate how many products to show initially
  const getInitialVisibleCount = () => {
    return getItemsPerRow() * 4; // Show 4 rows initially
  };

  useEffect(() => {
    setVisibleProducts(getInitialVisibleCount());
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    dispatch(getProducts());
  }, [dispatch]);

  const categories = ["All", "PCs", "Consoles", "Controllers", "Headphones"];

  const filteredAndSortedProducts = React.useMemo(() => {
    if (!products?.data) return [];

    let items = [...products.data];

    // Category Filter
    if (activeCategory !== "All") {
      items = items.filter(
        (product) =>
          (product.category || product.categoryName || "").toLowerCase() ===
          activeCategory.toLowerCase()
      );
    }

    // Sort
    if (sortBy === "title-ascending" || sortBy === "manual") {
      items.sort((a, b) =>
        (a.name || a.title || "").localeCompare(b.name || b.title || "")
      );
    } else if (sortBy === "title-descending") {
      items.sort((a, b) =>
        (b.name || b.title || "").localeCompare(a.name || a.title || "")
      );
    } else if (sortBy === "price-ascending") {
      items.sort((a, b) => parseFloat(a.price || 0) - parseFloat(b.price || 0));
    } else if (sortBy === "price-descending") {
      items.sort((a, b) => parseFloat(b.price || 0) - parseFloat(a.price || 0));
    } else if (sortBy === "created-ascending") {
      items.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    } else if (sortBy === "created-descending") {
      items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }

    return items;
  }, [products?.data, sortBy, activeCategory]);

  return (
    <div className="min-h-screen bg-[#0b0e14] py-16 px-4 relative overflow-hidden">
      <AnimatedBackground intensity={0.25} />
      <div className="max-w-6xl mx-auto w-full relative z-10">
        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mb-16"
        >
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-cyan-500 font-extrabold uppercase tracking-[0.5em] text-[10px] mb-4 block"
          >
            Hardware Arsenal
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6, ease: "easeOut" }}
            className="text-4xl md:text-7xl font-black text-white uppercase italic tracking-tighter"
          >
            System{" "}
            <span className="text-cyan-400 tracking-tighter">Inventory</span>
          </motion.h1>
        </motion.header>

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8 mb-12">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsFilterOpen(true)}
              className="lg:hidden flex items-center gap-2 sm:gap-3 px-6 sm:px-8 py-3 sm:py-4 bg-white/5 border border-white/10 rounded-2xl text-white font-black uppercase tracking-widest text-[10px] sm:text-xs hover:bg-white/10 transition active:scale-95"
              aria-label="Open filters"
            >
              <BsFilterLeft className="text-lg sm:text-xl text-cyan-400" />
              <span>Filters</span>
            </button>

            <div className="hidden lg:flex items-center gap-3 bg-black/40 p-2 rounded-2xl border border-white/5">
              {categories.map((cat, index) => (
                <motion.button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-[0.15em] transition-all ${
                    activeCategory === cat
                      ? "bg-cyan-500 text-black shadow-[0_0_15px_rgba(0,242,255,0.3)]"
                      : "text-gray-500 hover:text-white"
                  }`}
                >
                  {cat}
                </motion.button>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
              <span className="text-gray-500 font-black text-[10px] uppercase tracking-[0.2em] sm:whitespace-nowrap">
                Sort
              </span>
              <select
                name="sort_by"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full sm:w-auto px-4 sm:px-6 py-3 border border-white/10 rounded-2xl bg-white/5 text-white font-black text-xs uppercase tracking-widest focus:outline-none focus:border-cyan-400/50 transition-all cursor-pointer shadow-xl appearance-none pr-10 sm:pr-12 min-w-[200px]"
              >
                <option value="manual" className="bg-[#0b0e14]">
                  Priority
                </option>
                <option value="title-ascending" className="bg-[#0b0e14]">
                  A-Z Alpha
                </option>
                <option value="title-descending" className="bg-[#0b0e14]">
                  Z-A Omega
                </option>
                <option value="price-ascending" className="bg-[#0b0e14]">
                  Price Low-High
                </option>
                <option value="price-descending" className="bg-[#0b0e14]">
                  Price High-Low
                </option>
              </select>
            </div>
            <p className="text-cyan-500/50 font-black text-[10px] uppercase tracking-[0.3em] sm:border-l sm:border-white/10 sm:pl-6 text-center sm:text-left">
              {filteredAndSortedProducts.length} Units Found
            </p>
          </div>
        </div>

        <div className="w-full py-8">
          <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {isLoading && (
              <>
                {[...Array(6)].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1, duration: 0.4 }}
                  >
                    <ProductSkeleton />
                  </motion.div>
                ))}
              </>
            )}
            {isError && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-red-500 font-black uppercase tracking-widest"
              >
                {message}
              </motion.span>
            )}
            {!isLoading && !isError && filteredAndSortedProducts.length > 0
              ? filteredAndSortedProducts
                  .slice(0, visibleProducts)
                  .map((product, index) => (
                    <motion.div
                      key={product._id || product.id}
                      initial={{ opacity: 0, y: 30, scale: 0.9 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{
                        delay: index * 0.05,
                        duration: 0.5,
                        ease: [0.16, 1, 0.3, 1],
                      }}
                      whileHover={{ y: -5, transition: { duration: 0.2 } }}
                    >
                      <Product product={product} cart={cart} />
                    </motion.div>
                  ))
              : !isLoading &&
                !isError && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4 }}
                    className="text-center col-span-full py-20 bg-white/5 rounded-3xl border border-dashed border-white/10"
                  >
                    <p className="text-gray-500 font-black uppercase tracking-[0.2em] mb-6">
                      System database mismatch: No gear detected.
                    </p>
                    <Link
                      to="/catalog"
                      className="inline-flex items-center gap-3 text-cyan-400 font-black uppercase tracking-[0.3em] text-[10px] hover:text-cyan-300 transition-all"
                    >
                      Reset Inventory Query <HiOutlineArrowRight />
                    </Link>
                  </motion.div>
                )}
          </div>

          {!isLoading &&
            !isError &&
            filteredAndSortedProducts &&
            visibleProducts < filteredAndSortedProducts.length && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="mt-20 flex justify-center"
              >
                <motion.button
                  onClick={() =>
                    setVisibleProducts(visibleProducts + getItemsPerRow() * 2)
                  }
                  whileHover={{ scale: 1.05, backgroundColor: "#00f2ff" }}
                  whileTap={{ scale: 0.95 }}
                  className="px-12 py-5 bg-white text-black font-black uppercase tracking-[0.3em] text-[10px] rounded-2xl transition-all shadow-[0_0_30px_rgba(255,255,255,0.1)] flex items-center gap-3"
                >
                  Load More Hardware <HiOutlineArrowRight />
                </motion.button>
              </motion.div>
            )}
        </div>
      </div>

      {/* Mobile Filter Sidebar */}
      <AnimatePresence>
        {isFilterOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] lg:hidden"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/80 backdrop-blur-md"
              onClick={() => setIsFilterOpen(false)}
            />
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{
                type: "spring",
                damping: 25,
                stiffness: 200,
              }}
              className="absolute bottom-0 left-0 right-0 bg-[#0b0e14] border-t border-white/10 rounded-t-[40px] max-h-[85vh] flex flex-col"
            >
              <div className="flex justify-between items-center p-6 pb-4 border-b border-white/5 flex-shrink-0">
                <motion.h3
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                  className="text-lg sm:text-xl font-black text-white uppercase tracking-widest italic"
                >
                  Categories
                </motion.h3>
                <motion.button
                  initial={{ opacity: 0, rotate: -90 }}
                  animate={{ opacity: 1, rotate: 0 }}
                  transition={{ delay: 0.15 }}
                  whileHover={{ rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setIsFilterOpen(false)}
                  className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white/5 rounded-2xl text-gray-500 hover:bg-white/10 transition-colors"
                  aria-label="Close filter"
                >
                  <BsX className="text-2xl sm:text-3xl" />
                </motion.button>
              </div>
              <div className="overflow-y-auto custom-scrollbar flex-1 px-4 sm:px-6 py-6">
                <div className="grid grid-cols-1 gap-3 sm:gap-4">
                  {categories.map((cat, index) => (
                    <motion.button
                      key={cat}
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        delay: 0.2 + index * 0.05,
                        duration: 0.4,
                        ease: [0.16, 1, 0.3, 1],
                      }}
                      whileHover={{ scale: 1.02, x: 5 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        setActiveCategory(cat);
                        setIsFilterOpen(false);
                      }}
                      className={`w-full text-left py-4 sm:py-6 px-6 sm:px-8 rounded-2xl font-black uppercase tracking-[0.2em] text-[10px] transition-all border ${
                        activeCategory === cat
                          ? "bg-cyan-500 border-cyan-400 text-black shadow-[0_0_20px_rgba(0,242,255,0.2)]"
                          : "bg-white/5 border-transparent text-gray-500 hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      {cat}
                    </motion.button>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Catalog;
